import React, { useState, useEffect } from 'react';
import { Shield, Send, CheckCircle, XCircle, Clock, Users, Play, RefreshCw, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LegalGatePanel({ campaignId, onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [expiring, setExpiring] = useState(false);

  useEffect(() => {
    if (campaignId) fetchStatus();
  }, [campaignId]);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/reactivation/campaigns/${campaignId}/legal-gate/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
      if (onStatusChange) onStatusChange(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const startLegalGate = async (sendImmediately = false) => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/reactivation/campaigns/${campaignId}/legal-gate/start?send_immediately=${sendImmediately}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Legal Gate iniciado');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar');
    } finally {
      setSending(false);
    }
  };

  const sendBatch = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/reactivation/campaigns/${campaignId}/legal-gate/send-batch?batch_size=50`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${res.data.sent} SMS enviados`);
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar');
    } finally {
      setSending(false);
    }
  };

  const expireContacts = async () => {
    setExpiring(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/reactivation/campaigns/${campaignId}/legal-gate/expire`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${res.data.expired_count} contactos expirados`);
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setExpiring(false);
    }
  };

  if (loading) return <div className="text-gray-400 p-4">Cargando...</div>;

  const stats = status?.stats || {};
  const legalGateStatus = status?.legal_gate_status || 'not_started';

  const getStatusColor = () => {
    switch (legalGateStatus) {
      case 'completed': return 'text-emerald-400';
      case 'in_progress': return 'text-yellow-400';
      case 'pending': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusText = () => {
    switch (legalGateStatus) {
      case 'completed': return 'Completado';
      case 'in_progress': return 'En Progreso';
      case 'pending': return 'Pendiente';
      default: return 'No Iniciado';
    }
  };

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-emerald-400" />
          <div>
            <h3 className="font-semibold text-white">Legal Gate (RGPD)</h3>
            <p className="text-sm text-gray-400">Saneamiento de lista de contactos</p>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor()} bg-gray-900`}>
          {getStatusText()}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-900/50 rounded-lg p-3 text-center">
          <Users className="h-5 w-5 text-gray-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-white">{stats.total || 0}</div>
          <div className="text-xs text-gray-500">Total</div>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-3 text-center">
          <Send className="h-5 w-5 text-blue-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-blue-400">{stats.consent_sent || 0}</div>
          <div className="text-xs text-gray-500">SMS Enviados</div>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-3 text-center">
          <CheckCircle className="h-5 w-5 text-emerald-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-emerald-400">{stats.consent_confirmed || 0}</div>
          <div className="text-xs text-gray-500">Confirmados</div>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-3 text-center">
          <XCircle className="h-5 w-5 text-red-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-red-400">{(stats.consent_denied || 0) + (stats.consent_expired || 0)}</div>
          <div className="text-xs text-gray-500">Purgados</div>
        </div>
      </div>

      {/* Progress Bar */}
      {stats.total > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-gray-400">
            <span>Progreso del saneamiento</span>
            <span>{Math.round(((stats.consent_confirmed || 0) + (stats.consent_denied || 0) + (stats.consent_expired || 0)) / stats.total * 100)}%</span>
          </div>
          <div className="h-2 bg-gray-900 rounded-full overflow-hidden flex">
            <div 
              className="bg-emerald-500 h-full transition-all" 
              style={{ width: `${(stats.consent_confirmed || 0) / stats.total * 100}%` }}
            />
            <div 
              className="bg-red-500 h-full transition-all" 
              style={{ width: `${((stats.consent_denied || 0) + (stats.consent_expired || 0)) / stats.total * 100}%` }}
            />
            <div 
              className="bg-blue-500 h-full transition-all" 
              style={{ width: `${(stats.consent_sent || 0) / stats.total * 100}%` }}
            />
          </div>
          <div className="flex gap-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full"/>Confirmados</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full"/>Purgados</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-full"/>Esperando</span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-2">
        {legalGateStatus === 'not_started' && (
          <>
            <Button 
              onClick={() => startLegalGate(false)} 
              disabled={sending}
              className="bg-blue-600 hover:bg-blue-500"
            >
              <Play className="w-4 h-4 mr-2" />
              Iniciar Legal Gate
            </Button>
            <Button 
              onClick={() => startLegalGate(true)} 
              disabled={sending}
              variant="outline"
            >
              <Send className="w-4 h-4 mr-2" />
              Iniciar + Enviar SMS
            </Button>
          </>
        )}
        
        {(legalGateStatus === 'pending' || legalGateStatus === 'in_progress') && (
          <>
            <Button 
              onClick={sendBatch} 
              disabled={sending || (stats.consent_pending || 0) === 0}
              className="bg-blue-600 hover:bg-blue-500"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Enviando...' : `Enviar Lote (${stats.consent_pending || 0} pendientes)`}
            </Button>
            <Button 
              onClick={expireContacts} 
              disabled={expiring}
              variant="outline"
              className="text-orange-400 border-orange-400/50"
            >
              <Clock className="w-4 h-4 mr-2" />
              {expiring ? 'Procesando...' : 'Expirar Sin Respuesta'}
            </Button>
          </>
        )}

        {legalGateStatus === 'completed' && (
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{stats.ready_for_sale || stats.consent_confirmed || 0} contactos listos para venta</span>
          </div>
        )}

        <Button onClick={fetchStatus} variant="ghost" size="sm">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Info Box */}
      {legalGateStatus === 'not_started' && (
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-200">
            <strong>RGPD/PECR/TCPA:</strong> Antes de contactar a los clientes con ofertas comerciales, 
            debes obtener su consentimiento explícito. El Legal Gate envía un SMS preguntando si desean 
            recibir promociones. Solo los que respondan "SI" pasarán a la fase de venta.
          </div>
        </div>
      )}
    </div>
  );
}

export default LegalGatePanel;
